// Copyright (c) 2025. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import { useState, useCallback, useEffect, useMemo } from 'react';
import { getDoc, onSnapshot } from 'firebase/firestore';

export function useGetDocument(ref, { withSnapshot = false, initialCall = true, dependencies = [] } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(initialCall);
  const [error, setError] = useState(null);

  const stableRef = useMemo(() => ref, [JSON.stringify(ref)]);

  const getDocument = useCallback(async () => {
    if (!stableRef) return null;
    setLoading(true);
    setError(null);
    try {
      const snapshot = await getDoc(stableRef);
      const docData = snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
      setData(docData);
      return docData;
    } catch (err) {
      setError(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [stableRef]);

  const getDocumentWithSnapshot = useCallback(() => {
    if (!stableRef) return () => {};
    setLoading(true);
    setError(null);

    const unsubscribe = onSnapshot(
      stableRef,
      (snapshot) => {
        const docData = snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
        setData(docData);
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      },
    );

    return unsubscribe;
  }, [stableRef]);

  useEffect(() => {
    if (!initialCall) return;

    if (withSnapshot) {
      const unsubscribe = getDocumentWithSnapshot();
      return () => unsubscribe?.();
    } else {
      getDocument();
    }
  }, [stableRef, withSnapshot, initialCall, ...dependencies]);

  return { data, loading, error, getDocument: withSnapshot ? getDocumentWithSnapshot : getDocument };
}
